import React from 'react';
import { Badge } from './ui/badge';
import { CheckCircle2, Shield, Award, Star, Verified } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * VerificationBadge - Trust and verification badge component
 * Displays various types of verification badges with icons
 * 
 * @param {Object} props
 * @param {string} props.type - Badge type: 'id', 'background', 'premium', 'verified', 'trusted'
 * @param {string} props.label - Badge label text
 * @param {boolean} props.showIcon - Whether to show icon (default: true)
 * @param {string} props.size - Badge size: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} props.variant - Badge variant: 'default', 'outline', 'secondary'
 * @param {string} props.className - Additional CSS classes
 */
const VerificationBadge = ({
  type = 'verified',
  label,
  showIcon = true,
  size = 'md',
  variant = 'default',
  className,
  ...props
}) => {
  // Badge configuration based on type
  const badgeConfig = {
    id: {
      icon: CheckCircle2,
      defaultLabel: 'ID Verified',
      color: 'bg-success-500 text-success-foreground hover:bg-success-600',
      outlineColor: 'border-success-500 text-success-700 hover:bg-success-50',
    },
    background: {
      icon: Shield,
      defaultLabel: 'Background Check',
      color: 'bg-primary-500 text-primary-foreground hover:bg-primary-600',
      outlineColor: 'border-primary-500 text-primary-700 hover:bg-primary-50',
    },
    premium: {
      icon: Award,
      defaultLabel: 'Premium Member',
      color: 'bg-secondary-500 text-secondary-foreground hover:bg-secondary-600',
      outlineColor: 'border-secondary-500 text-secondary-700 hover:bg-secondary-50',
    },
    verified: {
      icon: Verified,
      defaultLabel: 'Verified',
      color: 'bg-primary-500 text-primary-foreground hover:bg-primary-600',
      outlineColor: 'border-primary-500 text-primary-700 hover:bg-primary-50',
    },
    trusted: {
      icon: Star,
      defaultLabel: 'Trusted Host',
      color: 'bg-secondary-500 text-secondary-foreground hover:bg-secondary-600',
      outlineColor: 'border-secondary-500 text-secondary-700 hover:bg-secondary-50',
    },
  };

  const config = badgeConfig[type] || badgeConfig.verified;
  const Icon = config.icon;
  const displayLabel = label || config.defaultLabel;

  // Size configurations
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // Determine color classes based on variant
  const colorClasses = variant === 'outline' ? config.outlineColor : config.color;

  return (
    <Badge
      variant={variant}
      className={cn(
        'inline-flex items-center gap-1.5 font-medium transition-all duration-200',
        sizeClasses[size],
        variant === 'default' && colorClasses,
        variant === 'outline' && `${colorClasses} border-2`,
        className
      )}
      {...props}
    >
      {showIcon && <Icon className={cn('flex-shrink-0', iconSizes[size])} />}
      <span>{displayLabel}</span>
    </Badge>
  );
};

/**
 * VerificationBadgeGroup - Group of verification badges
 * Displays multiple verification badges in a flex container
 * 
 * @param {Object} props
 * @param {Array} props.badges - Array of badge configurations
 * @param {string} props.size - Size for all badges
 * @param {string} props.variant - Variant for all badges
 * @param {string} props.className - Additional CSS classes
 */
export const VerificationBadgeGroup = ({
  badges = [],
  size = 'md',
  variant = 'default',
  className,
  ...props
}) => {
  return (
    <div className={cn('flex flex-wrap gap-2', className)} {...props}>
      {badges.map((badge, index) => (
        <VerificationBadge
          key={index}
          type={badge.type}
          label={badge.label}
          showIcon={badge.showIcon}
          size={size}
          variant={variant}
        />
      ))}
    </div>
  );
};

export default VerificationBadge;